/**
 * CorrectionsBlock — the Work-page "Corrections & Decisions" panel.
 *
 * Surfaces the captured signals that train the system for this item:
 *   - product-intent / in-loop steering `yagni_code_decisions` (incl. the
 *     `cli_correction`s recorded mid-`/go`, attributed to a run + stage), and
 *   - merged-PR implementation corrections (`code_captured_edits`).
 *
 * Reads `GET /api/work-items/:id/corrections` (customer-scoped, normal app
 * auth). Renders nothing when both lists are empty, so it only appears once
 * there is something to show. Refetches on a live `correction_captured`
 * activity beat for this item (mirrors the useWorkItemActivity subscription).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { decisionUrl } from "@yagni/shared";
import { sourceLabel } from "../ledger/decisionCopy.js";

interface CorrectionDecision {
  id: string;
  question?: string | null;
  decision: string;
  rationale?: string | null;
  run_id?: string | null;
  stage?: string | null;
  source?: string | null;
  created_at?: string | null;
}

interface CorrectionEdit {
  id: string;
  repo_full_name?: string | null;
  pr_number?: number | null;
  edit_distance?: number | null;
  reason?: string | null;
  run_id?: string | null;
  captured_at?: string | null;
}

interface CorrectionsPayload {
  decisions: CorrectionDecision[];
  edits: CorrectionEdit[];
}

export function CorrectionsBlock({
  workItemId,
  skipSources,
}: {
  workItemId: string;
  /**
   * Decision sources another section of the page already shows. The case file
   * skips the plan's own answers (`plan_gate_answer`), which read under the
   * plan as "Your answers".
   */
  skipSources?: readonly string[];
}) {
  const [data, setData] = useState<CorrectionsPayload>({
    decisions: [],
    edits: [],
  });

  const load = useCallback(() => {
    let cancelled = false;
    fetch(`/api/work-items/${encodeURIComponent(workItemId)}/corrections`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? (r.json() as Promise<Partial<CorrectionsPayload>>) : null))
      .then((payload) => {
        if (cancelled || !payload) return;
        setData({
          decisions: Array.isArray(payload.decisions) ? payload.decisions : [],
          edits: Array.isArray(payload.edits) ? payload.edits : [],
        });
      })
      .catch(() => {
        // Empty render is fine — never break the page on a corrections miss.
      });
    return () => {
      cancelled = true;
    };
  }, [workItemId]);

  useEffect(() => load(), [load]);

  // Live beat: when a correction lands on this item, refetch so the panel
  // stays current without a page reload.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          workItemId?: string;
          activity?: { type?: string };
        }>
      ).detail;
      if (!detail || detail.workItemId !== workItemId) return;
      if (detail.activity?.type === "correction_captured") load();
    };
    window.addEventListener("work_item_activity.created", handler);
    return () =>
      window.removeEventListener("work_item_activity.created", handler);
  }, [workItemId, load]);

  const { edits } = data;
  const decisions = skipSources?.length
    ? data.decisions.filter((d) => !d.source || !skipSources.includes(d.source))
    : data.decisions;
  if (decisions.length === 0 && edits.length === 0) return null;

  return (
    <div className="wk-corr">
      <div className="is-art-lab">Corrections &amp; decisions</div>
      <div className="wk-corr-list">
        {decisions.map((d) => (
          <div className="wk-corr-row" key={`d-${d.id}`}>
            <span className="wk-corr-dot" aria-hidden />
            <div className="wk-corr-main">
              {/* The row opens the record on the Decisions surface, where it
                  can be confirmed, superseded, or discarded. */}
              <Link className="wk-corr-text wk-corr-link" to={decisionUrl(d.id)}>
                {d.decision}
              </Link>
              <div className="wk-corr-meta">
                {d.source && <span className="wk-corr-src">{sourceLabel(d.source)}</span>}
                {d.stage && (
                  <>
                    <span className="wk-corr-sep" aria-hidden />
                    <span className="wk-corr-stage">{d.stage}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
        {edits.map((e) => (
          <div className="wk-corr-row" key={`e-${e.id}`}>
            <span className="wk-corr-dot is-edit" aria-hidden />
            <div className="wk-corr-main">
              <div className="wk-corr-text">
                {e.reason && e.reason.trim()
                  ? e.reason
                  : "Merged-PR correction"}
              </div>
              <div className="wk-corr-meta">
                {e.repo_full_name && (
                  <span className="wk-corr-src">{e.repo_full_name}</span>
                )}
                {typeof e.pr_number === "number" && (
                  <>
                    <span className="wk-corr-sep" aria-hidden />
                    <span className="wk-corr-stage">#{e.pr_number}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
